import { z } from 'zod';

/**
 * Ledger overlays: venue-only changes on top of the corporate template, entered by the requester
 * on the "Ledger overlays" step (or the request workbook's overlay sheets), flagged as deviations
 * at review, written into the venue's ledger package, and checked in the validation report.
 *
 * - Overlay B, journal names: a journal name the template does not have, with journal control
 *   limiting it to a main account range.
 * - Overlay C, main account overrides: accounts suspended for the venue only, and default
 *   dimension values on a main account for the venue only. The chart of accounts is shared, so
 *   these are legal entity overrides: the template company and other venues are untouched.
 *
 * Stored with the request as answers (`ledger.journals`, `ledger.accountOverrides`), so a request
 * file downloaded from one venue carries its overlays to the next.
 */

/**
 * What a template company's ledger already has, for the "differs from template" checks, and where
 * an account overlay's default dimension goes. Mirrors the template profiles
 * (packages/core/src/package/templateProfiles.ts; a core test keeps them in step).
 */
export interface TemplateLedger {
  company: string;
  journalNames: readonly string[];
  /** Main accounts the template already overrides for its own company */
  overriddenAccounts: readonly string[];
  /** The template's main accounts are six digits (chart Shared) */
  mainAccountPattern: RegExp;
  /**
   * The dimension an account overlay defaults: its slot in the default dimension display value
   * (the data entity format, segments joined by "-") and the number of slots.
   */
  defaultDimension: { name: string; segment: number; segments: number };
  /** The restricted cash journal the wizard offers as an example, on this chart's cash accounts */
  exampleJournal: { name: string; description: string; fromAccount: string; toAccount: string };
}

export const TEMPLATE_LEDGERS = {
  // USMF in the RSM sandbox: "001---", BusinessUnit first of 4. VERIFY the segment name.
  USMF: {
    company: 'USMF',
    journalNames: ['GenJrn', 'WFGenJrn', 'PerJrn', 'Allocation', 'IntJrn', 'WriteOff', 'STAT', 'Rev Rec'],
    overriddenAccounts: ['110110', '150100', '510360', '600500', '618160'],
    mainAccountPattern: /^\d{6}$/,
    defaultDimension: { name: 'BusinessUnit', segment: 0, segments: 4 },
    exampleJournal: {
      name: 'RESTRICT',
      description: 'Restricted cash journal',
      fromAccount: '110100',
      toAccount: '110199',
    },
  },
  // 1100 in the Legends TRN. Default dimension format for data entities (queried 2026-10-27):
  // MainAccount-ContractLocation-ContractId-LocationId-ChannelCategory-ServiceCategory-Department-
  // ICCounterparty, so 1100's "----C9900-S1000--" is Channel C9900, Service S1000. Overlays set
  // ServiceCategory (S1000, S1100, ... exist).
  '1100': {
    company: '1100',
    journalNames: [
      'GenJrn',
      'AllocJrn',
      'BegBal',
      'ConsoJrn',
      'Def_Exp',
      'NoRecurJrn',
      'PayrollJrn',
      'RecurJrn',
      'RevJrn',
      'TaxJrn',
      'VenJrn',
    ],
    overriddenAccounts: [
      '201010',
      '201020',
      '201030',
      '201040',
      '201050',
      '201100',
      '201110',
      '201112',
      '201114',
      '201120',
      '201200',
      '201300',
      '201400',
      '201410',
      '201420',
      '201500',
      '714900',
      '723100',
      '732900',
      '741900',
      '753100',
      '762100',
      '776200',
    ],
    mainAccountPattern: /^\d{6}$/,
    defaultDimension: { name: 'ServiceCategory', segment: 5, segments: 8 },
    // 108000 Restricted Cash … 108900 Restricted Cash - Contra (chart Shared, TRN 2026-10-27)
    exampleJournal: {
      name: 'RESTRICT',
      description: 'Restricted cash journal',
      fromAccount: '108000',
      toAccount: '108900',
    },
  },
} as const satisfies Record<string, TemplateLedger>;

/** The RSM sandbox's (USMF) ledger: the default wherever no template company is known. */
export const TEMPLATE_LEDGER: TemplateLedger = TEMPLATE_LEDGERS.USMF;

/** The ledger rules for a template company (an environment profile's templateCompany), or USMF's. */
export function templateLedgerFor(company: string | undefined): TemplateLedger {
  return company && Object.hasOwn(TEMPLATE_LEDGERS, company)
    ? TEMPLATE_LEDGERS[company as keyof typeof TEMPLATE_LEDGERS]
    : TEMPLATE_LEDGER;
}

export const JournalOverlay = z.object({
  name: z.string(),
  description: z.string(),
  fromAccount: z.string(),
  toAccount: z.string(),
});
export type JournalOverlay = z.infer<typeof JournalOverlay>;

export const AccountOverlay = z.object({
  mainAccountId: z.string(),
  suspend: z.boolean(),
  /** Default value of the template's overlay dimension here; blank leaves the default alone */
  defaultDimensionValue: z.string(),
});
export type AccountOverlay = z.infer<typeof AccountOverlay>;

/**
 * The default dimension display value with the overlay dimension set to `value` ("002---" in
 * USMF, "-----S1100--" in 1100). On an existing value (the template's override row) only that slot
 * changes, so its other dimensions stay; a value of another shape is replaced.
 */
export function defaultDimensionDisplay(
  value: string,
  ledger: TemplateLedger = TEMPLATE_LEDGER,
  existing = '',
): string {
  if (!value) return '';
  const { segment, segments } = ledger.defaultDimension;
  const parts = existing.split('-');
  const slots = parts.length === segments ? parts : Array.from({ length: segments }, () => '');
  return slots.map((v, i) => (i === segment ? value : v)).join('-');
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Rows as typed or loaded, trimmed; fully blank rows dropped. */
export function normalizeJournalOverlays(value: unknown): JournalOverlay[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const row = {
      name: str(r['name']),
      description: str(r['description']),
      fromAccount: str(r['fromAccount']),
      toAccount: str(r['toAccount']),
    };
    return Object.values(row).some(Boolean) ? [row] : [];
  });
}

export function normalizeAccountOverlays(value: unknown): AccountOverlay[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const row = {
      mainAccountId: str(r['mainAccountId']),
      suspend: r['suspend'] === true,
      defaultDimensionValue: str(r['defaultDimensionValue']).toUpperCase(),
    };
    return row.mainAccountId || row.defaultDimensionValue || row.suspend ? [row] : [];
  });
}

const accountCheck =
  (ledger: TemplateLedger) =>
  (id: string): string | null =>
    ledger.mainAccountPattern.test(id) ? null : `"${id || '(blank)'}" is not a main account (6 digits)`;

export function journalOverlayErrors(value: unknown, ledger: TemplateLedger = TEMPLATE_LEDGER): string[] {
  const rows = normalizeJournalOverlays(value);
  const errors: string[] = [];
  const seen = new Set<string>();
  const account = accountCheck(ledger);
  const template = new Set(ledger.journalNames.map((n) => n.toLowerCase()));
  rows.forEach((r, i) => {
    const at = `Journal ${i + 1}`;
    if (!/^[A-Za-z0-9_-]{1,10}$/.test(r.name))
      errors.push(`${at}: name is 1–10 letters, digits, - or _ (D365 journal name)`);
    else if (template.has(r.name.toLowerCase()))
      errors.push(`${at}: ${r.name} is already a template journal; an overlay adds a new one`);
    else if (seen.has(r.name.toLowerCase())) errors.push(`${at}: ${r.name} is listed twice`);
    seen.add(r.name.toLowerCase());
    if (!r.description) errors.push(`${at}: add a description`);
    else if (r.description.length > 60) errors.push(`${at}: description is at most 60 characters`);
    const from = account(r.fromAccount);
    const to = account(r.toAccount);
    if (from) errors.push(`${at}: from ${from}`);
    if (to) errors.push(`${at}: to ${to}`);
    if (!from && !to && r.fromAccount > r.toAccount)
      errors.push(`${at}: the range starts after it ends (${r.fromAccount}–${r.toAccount})`);
  });
  return errors;
}

export function accountOverlayErrors(value: unknown, ledger: TemplateLedger = TEMPLATE_LEDGER): string[] {
  const rows = normalizeAccountOverlays(value);
  const errors: string[] = [];
  const seen = new Set<string>();
  const account = accountCheck(ledger);
  const dim = ledger.defaultDimension.name;
  rows.forEach((r, i) => {
    const at = `Account ${i + 1}`;
    const bad = account(r.mainAccountId);
    if (bad) errors.push(`${at}: ${bad}`);
    else if (seen.has(r.mainAccountId)) errors.push(`${at}: ${r.mainAccountId} is listed twice`);
    seen.add(r.mainAccountId);
    if (!r.suspend && !r.defaultDimensionValue)
      errors.push(`${at}: suspend it, give it a default ${dim}, or remove the row`);
    if (r.defaultDimensionValue && !/^[A-Z0-9_-]{1,20}$/.test(r.defaultDimensionValue))
      errors.push(`${at}: ${dim} value is 1–20 letters or digits`);
  });
  return errors;
}

export function describeJournalOverlay(r: JournalOverlay): string {
  return `New journal ${r.name} (${r.description}): posts only to main accounts ${r.fromAccount}–${r.toAccount}`;
}

export function describeAccountOverlay(r: AccountOverlay, ledger: TemplateLedger = TEMPLATE_LEDGER): string {
  const parts = [
    r.suspend ? 'suspended' : null,
    r.defaultDimensionValue ? `default ${ledger.defaultDimension.name} ${r.defaultDimensionValue}` : null,
  ].filter(Boolean);
  return `Main account ${r.mainAccountId}: ${parts.join(', ')} (this venue only)`;
}
